import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import DefaultSelectMenu from 'components/selectMenu/DefaultSelectMenu';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';

interface MonthlySalesData {
  [key: string]: number;
}

interface MonthlySalesDetailsProps {
  companyInfo: CompanyInfor;
}

const MonthlySalesDetails = ({ companyInfo }: MonthlySalesDetailsProps) => {
  const date = new Date();
  const currentYear = date.getFullYear();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const requestYearsList = useQuery({
    queryKey: [QueryKeys.SAELS_YEARS],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/sales/service/year',
        params: {
          companyidx: companyInfo.companyidx
        }
      })
  });

  const yearsList = requestYearsList?.data?.yearlist;

  const yearsListOptions = yearsList?.map((year: number) => ({
    label: year.toString(),
    value: year
  }));

  const requestMonthlySalesList = useQuery({
    queryKey: [QueryKeys.SALES_MONTHLY],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/sales/annualsales',
        params: {
          companyidx: companyInfo.companyidx,
          year: selectedYear
        }
      })
  });

  const { data: monthlySalesData } = requestMonthlySalesList;

  const monthlySalesList = monthlySalesData?.monthly as MonthlySalesData;

  const totalPrice = Object.values(monthlySalesList || {}).reduce((acc: any, price: any) => {
    if (!isNaN(price)) {
      acc += Number(price);
    }
    return acc;
  }, 0);

  const handleClickSelectedYear = (index: string | number) => {
    setSelectedYear(index as number);
  };

  useEffect(() => {
    requestYearsList.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    requestMonthlySalesList.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  return (
    <Wrapper>
      <HeaderWrapper>
        <h2>누적매출</h2>
        <DefaultSelectMenu
          defaultValue="Year"
          options={yearsListOptions}
          value={selectedYear}
          onChange={handleClickSelectedYear}
        />
      </HeaderWrapper>
      <div>
        <MonthData>
          {Object.keys(monthlySalesList || {}).map((month: string) => {
            return (
              <div key={month}>
                <Month>{month}월</Month>
                <Price>
                  {monthlySalesList[month] === 0 ? '-' : monthlySalesList[month]?.toLocaleString()}
                </Price>
              </div>
            );
          })}
        </MonthData>
        <TotalPrice>
          <div>Total</div>
          <div>{totalPrice.toLocaleString()}</div>
        </TotalPrice>
      </div>
    </Wrapper>
  );
};

export default MonthlySalesDetails;

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .ant-table-wrapper .ant-table-tbody td {
    padding: 16px 12px;
    vertical-align: middle;
  }

  .ant-select-selection-item {
    font-size: 14px;
  }

  & > p {
    padding: 16px 0 24px;
  }

  table,
  table thead.ant-table-thead tr th {
    text-align: center;
  }

  table tbody td {
    height: 80px;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  .ant-select {
    width: 140px;
  }
`;

const MonthData = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  text-align: center;

  & > div {
    width: calc(100% / 6);
    border: 1px solid ${color.gray300};
    box-sizing: border-box;
  }

  & > div:not(:first-child):not(:nth-child(7n)) {
    border-left: none;
  }

  & > div:nth-last-child(-n + 6) {
    border-top: none;
  }
`;

const Month = styled.div`
  padding: 8px 12px;
  background: ${color.gray100};
  color: ${color.gray600};
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.3px;
  border-bottom: 1px solid ${color.gray300};
`;

const Price = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 72px;
  padding: 20px 12px;
  color: ${color.black2};
  ${font.caption1_regular};
`;

const TotalPrice = styled.div`
  display: flex;
  text-align: center;
  border: 1px solid var(--Gray-Gray-300, #e0e0e0);
  border-top: none;
  background: var(--Primary-Blue-50, #e3f2ff);

  & div {
    width: 50%;
    padding: 24px 12px;
    text-align: center;
    color: ${color.black2};
    ${font.h4_bold};

    &:first-of-type {
      border-right: 1px solid ${color.gray300};
    }
  }
`;
